import { useTranslation } from "@repo/shared/i18n";

/*
  The redux filter tables keep their English labels: for `useLabelAsId`
  filters the label is the value sent to the API, and useUrlFilters matches
  it from the URL. Display goes through this lookup, keyed by filter key and
  option label, so the ids never change. Attribute filters carry the labels
  dict text already and fall through untouched.
*/
const FILTER_LABEL_KEYS = {
  gender: "filters.gender.label",
  ownedOrEvolved: "filters.ownedOrEvolved.label",
  price: "filters.price",
  metaRating: "filters.metaRating",
  rating: "filters.rating",
  pastAndPresentClubs: "filters.pastAndPresentClubs.label",
  favoriteClubsAndCountries: "filters.favoriteClubsAndCountries.label",
  country: "filters.country",
  club: "filters.club",
  league: "filters.league",
  position: "filters.position",
  rarity: "filters.rarity",
  quality: "filters.quality.label",
  playStyles: "filters.playStyles",
  playStylesPlus: "filters.playStylesPlus",
  "body-types": "filters.bodyType",
  height: "filters.height",
  accelerates: "filters.accelerates.label",
  skillMoves: "filters.skillMoves",
  weakFoot: "filters.weakFoot",
  offBalanceDribbling: "filters.offBalanceDribbling",
  preferredFoot: "filters.preferredFoot.label",
  maxPlayStyles: "filters.maxPlayStyles",
  maxPlayStylesPlus: "filters.maxPlayStylesPlus",
  "max-chain-length": "filters.maxChainLength",
  "max-games": "filters.maxNumberOfGames",
} as const;

const OPTION_LABEL_KEYS = {
  gender: {
    Male: "filters.gender.male",
    Female: "filters.gender.female",
  },
  ownedOrEvolved: {
    "My Club": "filters.ownedOrEvolved.myClub",
    "My Evolutions": "filters.ownedOrEvolved.myEvolutions",
    Squad: "filters.ownedOrEvolved.squad",
  },
  pastAndPresentClubs: {
    "Past and Present": "filters.pastAndPresentClubs.option",
  },
  favoriteClubsAndCountries: {
    "My Favorites": "filters.favoriteClubsAndCountries.option",
  },
  accelerates: {
    Explosive: "filters.accelerates.explosive",
    Controlled: "filters.accelerates.controlled",
    Lengthy: "filters.accelerates.lengthy",
  },
  quality: {
    Gold: "filters.quality.gold",
    Silver: "filters.quality.silver",
    Bronze: "filters.quality.bronze",
  },
  preferredFoot: {
    Left: "filters.preferredFoot.left",
    Right: "filters.preferredFoot.right",
  },
} as const;

type FilterLabelKey =
  (typeof FILTER_LABEL_KEYS)[keyof typeof FILTER_LABEL_KEYS];
type OptionLabelKey = {
  [K in keyof typeof OPTION_LABEL_KEYS]: (typeof OPTION_LABEL_KEYS)[K][keyof (typeof OPTION_LABEL_KEYS)[K]];
}[keyof typeof OPTION_LABEL_KEYS];

export const useFilterLabels = () => {
  const { t } = useTranslation("players");
  const filterLabel = (filter: { key: string; label: string }) => {
    const key = (FILTER_LABEL_KEYS as Record<string, FilterLabelKey>)[
      filter.key
    ];
    return key ? t(key) : filter.label;
  };
  const optionLabel = (filterKey: string, option: { label: string }) => {
    const key = (
      OPTION_LABEL_KEYS as Record<string, Record<string, OptionLabelKey>>
    )[filterKey]?.[option.label];
    return key ? t(key) : option.label;
  };
  return { filterLabel, optionLabel };
};
